import { createAsyncThunk, createSlice } from "@reduxjs/toolkit"

import type { RootState } from "../../../store.ts"
import { logout } from "../../LoginPage/redux/auth.thunks.ts"
import { fetchUserInfo } from "../../User/redux/user.thunks.ts"
import { getCompanyDefaultsAccess } from "../api/company-defaults-access.services.ts"
import {
  COMPANY_DEFAULTS_BLOCK_ACTIONS,
  type CompanyDefaultsAccessResponse,
  type CompanyDefaultsBlockKey,
  type CompanyDefaultsBlocks
} from "../types/company-defaults-access.types.ts"

export interface CompanyDefaultsAccessState {
  access: CompanyDefaultsAccessResponse | null
  // The workspace `access` answers for; `null` is Company HQ.
  teamId: string | null
  status: "idle" | "loading" | "ready" | "error"
  requestId: string | null
  requestedTeamId: string | null
  requestedGeneration: number
  loadedGeneration: number
  // Bumped whenever the user's own permissions are reloaded, so the next read
  // asks the server again instead of trusting the answer it already has.
  generation: number
}

const initialState: CompanyDefaultsAccessState = {
  access: null,
  teamId: null,
  status: "idle",
  requestId: null,
  requestedTeamId: null,
  requestedGeneration: 0,
  loadedGeneration: -1,
  generation: 0
}

// Every known block with exactly its actions: a block the server leaves out
// grants nothing, and one this app does not know is dropped.
export const normalizeCompanyDefaultsBlocks = (
  served: unknown
): CompanyDefaultsBlocks => {
  const source = (served ?? {}) as Record<
    string,
    Record<string, unknown> | undefined
  >
  return Object.fromEntries(
    (
      Object.entries(COMPANY_DEFAULTS_BLOCK_ACTIONS) as Array<
        [CompanyDefaultsBlockKey, readonly string[]]
      >
    ).map(([block, actions]) => [
      block,
      Object.fromEntries(
        actions.map((action) => [action, source[block]?.[action] === true])
      )
    ])
  ) as CompanyDefaultsBlocks
}

interface FetchArgs {
  teamId: string | null
  force?: boolean
}

type ThunkConfig = { state: Pick<RootState, "companyDefaultsAccess"> }

export const fetchCompanyDefaultsAccess = createAsyncThunk<
  CompanyDefaultsAccessResponse,
  FetchArgs,
  ThunkConfig
>(
  "companyDefaultsAccess/fetch",
  async ({ teamId }, { rejectWithValue }) => {
    const response = await getCompanyDefaultsAccess(teamId)
    if (!response.ok || !response.data) {
      return rejectWithValue(response.status ?? null)
    }
    return {
      ...response.data,
      blocks: normalizeCompanyDefaultsBlocks(response.data.blocks)
    }
  },
  {
    condition: ({ teamId, force }, { getState }) => {
      if (force) return true
      const state = getState().companyDefaultsAccess
      if (!state) return true
      const current = state.generation
      if (
        state.requestId &&
        state.requestedTeamId === teamId &&
        state.requestedGeneration === current
      )
        return false
      return !(
        state.status === "ready" &&
        state.teamId === teamId &&
        state.loadedGeneration === current
      )
    }
  }
)

const companyDefaultsAccessSlice = createSlice({
  name: "companyDefaultsAccess",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchCompanyDefaultsAccess.pending, (state, action) => {
        state.requestId = action.meta.requestId
        state.requestedTeamId = action.meta.arg.teamId
        state.requestedGeneration = state.generation
        if (state.teamId !== action.meta.arg.teamId || !state.access) {
          state.status = "loading"
        }
      })
      .addCase(fetchCompanyDefaultsAccess.fulfilled, (state, action) => {
        if (state.requestId !== action.meta.requestId) return
        state.requestId = null
        state.access = action.payload
        state.teamId = action.meta.arg.teamId
        state.loadedGeneration = state.requestedGeneration
        state.status = "ready"
      })
      .addCase(fetchCompanyDefaultsAccess.rejected, (state, action) => {
        if (state.requestId !== action.meta.requestId) return
        state.requestId = null
        // A failed refresh of the workspace already shown keeps what it had;
        // the server still refuses anything the user may no longer do.
        if (state.access && state.teamId === action.meta.arg.teamId) return
        state.access = null
        state.teamId = action.meta.arg.teamId
        state.status = "error"
      })
      .addCase(fetchUserInfo.fulfilled, (state) => {
        state.generation += 1
      })
      .addCase(logout.fulfilled, () => initialState)
  }
})

export default companyDefaultsAccessSlice
